import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import type { MenuItem } from '../types/navigation'

export function useSubmenu() {
  const activeSubmenu = ref<string | null>(null)
  const isClicked = ref<boolean>(false)
  const submenuPosition = ref<number>(0)
  const containerRef = ref<HTMLDivElement | null>(null)
  const menuRefs = ref<{ [key: string]: HTMLAnchorElement | null }>({})

  const calculateSubmenuPosition = (menuKey: string) => {
    const menuElement = menuRefs.value[menuKey]
    const containerElement = containerRef.value
    
    if (menuElement && containerElement) {
      const menuRect = menuElement.getBoundingClientRect()
      const containerRect = containerElement.getBoundingClientRect()
      
      const menuCenter = menuRect.left + menuRect.width / 2 - containerRect.left
      const submenuWidth = containerRect.width * 0.3
      const leftPosition = menuCenter - submenuWidth / 2
      const maxLeft = containerRect.width * 0.9 - submenuWidth
      const minLeft = containerRect.width * 0.05
      
      return Math.max(minLeft, Math.min(maxLeft, leftPosition))
    }
    
    return 0
  }

  const handleMenuClick = (menuItem: MenuItem) => {
    if (menuItem.submenu && menuItem.submenu.length > 0) {
      if (activeSubmenu.value === menuItem.key && isClicked.value) {
        activeSubmenu.value = null
        isClicked.value = false
      } else {
        const position = calculateSubmenuPosition(menuItem.key)
        submenuPosition.value = position
        activeSubmenu.value = menuItem.key
        isClicked.value = true
      }
    } else {
      activeSubmenu.value = null
      isClicked.value = false
      
      if (menuItem.onClick) {
        menuItem.onClick()
      } else if (menuItem.href) {
        window.location.href = menuItem.href
      }
    }
  }

  const handleMenuHover = (menuItem: MenuItem) => {
    if (menuItem.submenu && menuItem.submenu.length > 0) {
      if (!isClicked.value) {
        const position = calculateSubmenuPosition(menuItem.key)
        submenuPosition.value = position
        activeSubmenu.value = menuItem.key
      }
    }
  }

  const handleMenuLeave = () => {
    if (!isClicked.value) {
      activeSubmenu.value = null
    }
  }

  const handleContainerLeave = () => {
    if (!isClicked.value) {
      activeSubmenu.value = null
    }
  }

  const closeSubmenu = () => {
    activeSubmenu.value = null
    isClicked.value = false
  }

  // Gestion des clics en dehors du menu
  const handleDocumentClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement

    const isNavElement = target.closest('[data-navbar-element]') || 
                        target.closest('.navbar-container') ||
                        target.closest('.submenu-container')

    if (!isNavElement && activeSubmenu.value) {
      closeSubmenu()
    }
  }

  // Gestion du redimensionnement
  const handleResize = () => {
    if (activeSubmenu.value) {
      const position = calculateSubmenuPosition(activeSubmenu.value)
      submenuPosition.value = position
    }
  }

  onMounted(() => {
    window.addEventListener('resize', handleResize)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
    document.removeEventListener('mousedown', handleDocumentClick)
  })

  // Watch pour activeSubmenu et ajouter/retirer le listener
  watch(activeSubmenu, (newValue) => {
    if (newValue) {
      document.addEventListener('mousedown', handleDocumentClick)
    } else {
      document.removeEventListener('mousedown', handleDocumentClick)
    }
  })

  return {
    activeSubmenu,
    isClicked,
    submenuPosition,
    containerRef,
    menuRefs,
    handleMenuClick,
    handleMenuHover,
    handleMenuLeave,
    handleContainerLeave,
    closeSubmenu
  }
}
